import { useEffect, useState } from 'react';

interface HeaderProps {
  query: string;
  onQuery: (v: string) => void;
}

const NAV = [
  { label: '今日热榜', href: '#hot' },
  { label: '最新热瓜', href: '#feed' },
  { label: '深扒专题', href: '#feature' },
  { label: '人气榜', href: '#rank' },
  { label: '大事记', href: '#timeline' },
  { label: '常见问题', href: '#faq' },
];

export default function Header({ query, onQuery }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = scrolled || open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? 'bg-background-50/90 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] items-center justify-between gap-4 px-4 md:px-6 lg:px-10">
        {/* Brand */}
        <a
          href="#top"
          className="flex items-center gap-2.5 whitespace-nowrap"
          aria-label="吃瓜八卦首页"
        >
          <span className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-lg md:text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading font-bold text-lg md:text-xl tracking-tight ${
                solid ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              吃瓜八卦
            </span>
            <span
              className={`font-label text-[10px] md:text-[11px] ${
                solid ? 'text-foreground-500' : 'text-background-100/70'
              }`}
            >
              明星 · 演员 · 歌手 · 爱豆
            </span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className={`px-3.5 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors hover:bg-primary-100/70 hover:text-primary-700 ${
                solid ? 'text-foreground-700' : 'text-background-50 hover:bg-background-50/10 hover:text-white'
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <div
            className={`hidden md:flex items-center gap-2 rounded-full border px-3.5 py-2 transition-colors ${
              solid
                ? 'border-background-200 bg-background-100 focus-within:border-primary-300'
                : 'border-background-50/40 bg-background-50/10 focus-within:border-background-50'
            }`}
          >
            <i
              className={`ri-search-line text-base ${solid ? 'text-foreground-500' : 'text-background-50'}`}
            ></i>
            <input
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder="搜一搜谁在风口浪尖…"
              className={`w-40 bg-transparent text-sm outline-none placeholder:font-label ${
                solid ? 'text-foreground-950' : 'text-background-50 placeholder:text-background-100/60'
              }`}
              aria-label="站内搜索"
            />
          </div>

          <a
            href="#about"
            className="hidden md:inline-flex items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors hover:opacity-90 bg-accent-500 text-background-50"
          >
            我要爆料
          </a>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden flex h-10 w-10 items-center justify-center rounded-md transition-colors ${
              solid ? 'text-foreground-950 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
            }`}
            aria-label="展开菜单"
            aria-expanded={open}
          >
            <i className={`ri-${open ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 pb-5 pt-2 md:px-6">
          <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3.5 py-2 mb-3">
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder="搜一搜谁在风口浪尖…"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none"
              aria-label="站内搜索"
            />
          </div>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-primary-100/70 hover:text-primary-700"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#about"
              onClick={() => setOpen(false)}
              className="mt-1 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50"
            >
              我要爆料
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}